import {
  DEFAULT_MESSAGE_TYPE,
  SPECIAL_VARIABLE_KEYS,
  MANUAL_SPECIAL_VARIABLE_KEYS,
  VALIDATION_TO_SPECIAL_VARIABLE,
  ACTION_TABLE_VALUE_MAP,
  SAVE_VARIABLE_ALIASES,
  CREDIT_DETAIL_VARIABLE,
  CREDIT_DETAIL_PLACEHOLDER_KEYS,
  QUOTA_OPTIONS_VARIABLE,
  stepUsesQuotaOptionsPlaceholder,
  CURRENT_BALANCE_VARIABLE,
  stepUsesCurrentBalancePlaceholder,
  MAX_APPROVED_EXTENSION_VARIABLE,
  stepUsesMaxApprovedExtensionPlaceholder,
  EXTENSION_QUOTA_OPTIONS_VARIABLE,
  stepUsesExtensionQuotaOptionsPlaceholder,
  extractPlaceholdersFromMessage,
  stepHasVideollamadaValidation,
} from "./whatsappFlowCatalogs";
import {
  conditionalMessagesFromEditorData,
  normalizeConditionalMessages,
  parseApiConditionalMessages,
  serializeConditionalMessagesForPayload,
} from "./whatsappFlowConditional";
import {
  normalizeStepsEditionList,
  parseApiStepsEdition,
  serializeStepsEditionForPayload,
} from "./whatsappFlowStepsEdition";
import {
  computeValidationRulesFromStepData,
  serializeValidationRules,
  getOptionsTokensFromRules,
} from "./whatsappFlowValidationCodec";
import { OPTION_HANDLE_PREFIX } from "./whatsappFlowOptionBranches";

const STORAGE_KEY = "whatsappFlowBuilder";

const stripBraces = (k) => String(k).replace(/^\{\{|\}\}$/g, "");

export const normalizeWhatsappStepData = (data) => {
  const base = data && typeof data === "object" ? data : {};
  const validationRules = computeValidationRulesFromStepData(base);
  const user = base.user || "COMMERCE";
  const hasVideollamada = stepHasVideollamadaValidation(validationRules);
  const autoCompleteOnEnter = hasVideollamada
    ? true
    : base.autoCompleteOnEnter === true;
  const autoCancelOnEnter = hasVideollamada
    ? false
    : base.autoCancelOnEnter === true;
  return {
    name: base.name ?? "",
    message: base.message ?? "",
    messageType: base.messageType || DEFAULT_MESSAGE_TYPE,
    validationRules,
    buttons: Array.isArray(base.buttons) ? base.buttons : [],
    templateName: base.templateName ?? "",
    parameterKeys: Array.isArray(base.parameterKeys) ? base.parameterKeys : [],
    manualSpecialVariables: Array.isArray(base.manualSpecialVariables)
      ? Array.from(
          new Set(
            base.manualSpecialVariables.filter((k) =>
              MANUAL_SPECIAL_VARIABLE_KEYS.includes(k)
            )
          )
        )
      : [],
    saveVariable: SAVE_VARIABLE_ALIASES[base.saveVariable] || base.saveVariable || "",
    actions: Array.isArray(base.actions) ? base.actions : [],
    description: base.description ?? "",
    templateTextError: base.templateTextError ?? "",
    user,
    releaseCommerceAfterResponse:
      user === "COMMERCE" && base.releaseCommerceAfterResponse === true,
    templateLanguage: base.templateLanguage || "es",
    transitionMessage: base.transitionMessage ?? "",
    templateRequiredButtonId: base.templateRequiredButtonId ?? "",
    waFlowId: base.waFlowId ?? "",
    autoCompleteOnEnter,
    autoCancelOnEnter,
    clientCompletionNotificationMessage: base.clientCompletionNotificationMessage ?? "",
    commerceCompletionNotificationMessage: base.commerceCompletionNotificationMessage ?? "",
    adminNotificationMessage: base.adminNotificationMessage ?? "",
    conditionalMessages: conditionalMessagesFromEditorData(base),
    stepsEdition: normalizeStepsEditionList(base.stepsEdition),
    autoOptionBranch:
      base.autoOptionBranch &&
      typeof base.autoOptionBranch === "object" &&
      base.autoOptionBranch.parentId
        ? {
            parentId: String(base.autoOptionBranch.parentId),
            index: Number(base.autoOptionBranch.index),
            label:
              base.autoOptionBranch.label != null ? String(base.autoOptionBranch.label) : "",
          }
        : undefined,
  };
};

export const normalizeWhatsappFlowNode = (node) => ({
  ...node,
  type: node.type || "whatsappStep",
  data: normalizeWhatsappStepData(node.data),
});

export const createWhatsappFlowNode = (id, stepNumber, position) => ({
  id,
  type: "whatsappStep",
  position,
  data: normalizeWhatsappStepData({
    name: `Paso ${stepNumber}`,
    message: "",
    messageType: DEFAULT_MESSAGE_TYPE,
    validationRules: [],
    buttons: [],
    templateName: "",
    parameterKeys: [],
    manualSpecialVariables: [],
    saveVariable: "",
    actions: [],
    description: "",
    templateTextError: "",
    user: "COMMERCE",
    releaseCommerceAfterResponse: false,
    templateLanguage: "es",
    transitionMessage: "",
    templateRequiredButtonId: "",
    waFlowId: "",
    stepsEdition: [],
    autoCompleteOnEnter: false,
    autoCancelOnEnter: false,
    clientCompletionNotificationMessage: "",
    commerceCompletionNotificationMessage: "",
    adminNotificationMessage: "",
  }),
});

/**
 * BFS desde el nodo raíz (sin incoming edges) para calcular el orden de los pasos.
 */
const computeNodeOrder = (nodes, edges) => {
  const nodeIds = new Set(nodes.map((n) => n.id));
  const incomingTargets = new Set(edges.filter((e) => nodeIds.has(e.target)).map((e) => e.target));
  const roots = nodes.filter((n) => !incomingTargets.has(n.id));
  const adjacency = {};
  for (const edge of edges) {
    if (!adjacency[edge.source]) adjacency[edge.source] = [];
    adjacency[edge.source].push(edge.target);
  }
  const order = {};
  let counter = 1;
  const visited = new Set();
  const queue = roots.length > 0 ? roots.map((n) => n.id) : (nodes[0] ? [nodes[0].id] : []);
  for (const id of queue) visited.add(id);

  while (queue.length > 0) {
    const current = queue.shift();
    order[current] = counter++;
    for (const neighbor of adjacency[current] || []) {
      if (!visited.has(neighbor)) {
        visited.add(neighbor);
        queue.push(neighbor);
      }
    }
  }

  for (const node of nodes) {
    if (!order[node.id]) order[node.id] = counter++;
  }
  return order;
};

export const buildWhatsappFlowPayload = ({
  name,
  description = "",
  flowRole = "PRIMARY",
  nodes,
  edges,
}) => {
  const idMap = {};
  nodes.forEach((node, idx) => { idMap[node.id] = idx + 1; });

  const nodeOrder = computeNodeOrder(nodes, edges);

  // Destino del edge lineal (handle por defecto) por nodo origen. Las ramas usan
  // `sourceHandle` con prefijo OPTION_HANDLE_PREFIX y se serializan aparte en `options`.
  const defaultTargetBySource = {};
  for (const e of edges) {
    if (
      (e.sourceHandle == null || e.sourceHandle === "") &&
      defaultTargetBySource[e.source] == null
    ) {
      defaultTargetBySource[e.source] = e.target;
    }
  }

  const step_conversations = nodes.map((node) => {
    const stepData = normalizeWhatsappStepData(node.data);
    const numericId = idMap[node.id];

    const optionLabels = getOptionsTokensFromRules(stepData.validationRules);
    const options = {};
    if (optionLabels.length > 0) {
      optionLabels.forEach((label, i) => {
        const handleId = `${OPTION_HANDLE_PREFIX}${i}`;
        const edge = edges.find((e) => e.source === node.id && e.sourceHandle === handleId);
        if (edge && idMap[edge.target] != null) {
          options[label] = idMap[edge.target];
        }
      });
    }

    const regularParams = stepData.parameterKeys.filter(
      (k) => !SPECIAL_VARIABLE_KEYS.includes(stripBraces(k))
    );

    const autoSpecials = (stepData.validationRules || [])
      .map((r) => VALIDATION_TO_SPECIAL_VARIABLE[r?.type])
      .filter(Boolean);

    const manualSpecials = (stepData.manualSpecialVariables || []).filter((k) =>
      MANUAL_SPECIAL_VARIABLE_KEYS.includes(k)
    );

    const conditionalMessages = normalizeConditionalMessages(
      stepData.conditionalMessages
    );

    const stepsEdition = serializeStepsEditionForPayload(stepData.stepsEdition, idMap);

    const notificationMessages = [
      stepData.clientCompletionNotificationMessage,
      stepData.commerceCompletionNotificationMessage,
      stepData.adminNotificationMessage,
    ];

    const usesCreditDetail =
      CREDIT_DETAIL_PLACEHOLDER_KEYS.some((key) =>
        (stepData.message || "").includes(`{{${key}}}`)
      ) ||
      conditionalMessages.some((entry) =>
        CREDIT_DETAIL_PLACEHOLDER_KEYS.some((key) =>
          (entry.conditionalMessage || "").includes(`{{${key}}}`)
        )
      ) ||
      notificationMessages.some((message) =>
        CREDIT_DETAIL_PLACEHOLDER_KEYS.some((key) =>
          (message || "").includes(`{{${key}}}`)
        )
      );

    const usesQuotaOptions =
      stepUsesQuotaOptionsPlaceholder(stepData) ||
      conditionalMessages.some((entry) =>
        stepUsesQuotaOptionsPlaceholder({
          message: entry.conditionalMessage,
          parameterKeys: entry.conditionalParameterKeys,
        })
      ) ||
      notificationMessages.some((message) =>
        stepUsesQuotaOptionsPlaceholder({ message, parameterKeys: [] })
      );

    const usesCurrentBalance =
      stepUsesCurrentBalancePlaceholder(stepData) ||
      conditionalMessages.some((entry) =>
        stepUsesCurrentBalancePlaceholder({
          message: entry.conditionalMessage,
          parameterKeys: entry.conditionalParameterKeys,
        })
      ) ||
      notificationMessages.some((message) =>
        stepUsesCurrentBalancePlaceholder({ message, parameterKeys: [] })
      );

    const usesMaxApprovedExtension =
      stepUsesMaxApprovedExtensionPlaceholder(stepData) ||
      conditionalMessages.some((entry) =>
        stepUsesMaxApprovedExtensionPlaceholder({
          message: entry.conditionalMessage,
          parameterKeys: entry.conditionalParameterKeys,
        })
      ) ||
      notificationMessages.some((message) =>
        stepUsesMaxApprovedExtensionPlaceholder({ message, parameterKeys: [] })
      );

    const usesExtensionQuotaOptions =
      stepUsesExtensionQuotaOptionsPlaceholder(stepData) ||
      conditionalMessages.some((entry) =>
        stepUsesExtensionQuotaOptionsPlaceholder({
          message: entry.conditionalMessage,
          parameterKeys: entry.conditionalParameterKeys,
        })
      ) ||
      notificationMessages.some((message) =>
        stepUsesExtensionQuotaOptionsPlaceholder({ message, parameterKeys: [] })
      );

    const specialParams = Array.from(
      new Set([
        ...autoSpecials,
        ...manualSpecials,
        ...(usesCreditDetail ? [CREDIT_DETAIL_VARIABLE] : []),
        ...(usesQuotaOptions ? [QUOTA_OPTIONS_VARIABLE] : []),
        ...(usesCurrentBalance ? [CURRENT_BALANCE_VARIABLE] : []),
        ...(usesMaxApprovedExtension ? [MAX_APPROVED_EXTENSION_VARIABLE] : []),
        ...(usesExtensionQuotaOptions ? [EXTENSION_QUOTA_OPTIONS_VARIABLE] : []),
      ])
    );

    let tableValue = "";
    if (Array.isArray(stepData.actions)) {
      for (const action of stepData.actions) {
        if (ACTION_TABLE_VALUE_MAP[action]) {
          tableValue = ACTION_TABLE_VALUE_MAP[action];
          break;
        }
      }
    }

    return {
      id: numericId,
      order: nodeOrder[node.id] || numericId,
      name: stepData.name,
      description: stepData.description,
      template: stepData.messageType,
      templateText: stepData.message,
      templateTextError: stepData.templateTextError,
      buttonsLabels: stepData.buttons.join(","),
      validations: serializeValidationRules(stepData.validationRules),
      options: Object.keys(options).length > 0 ? options : undefined,
      nextStep:
        Object.keys(options).length === 0 &&
        defaultTargetBySource[node.id] != null &&
        idMap[defaultTargetBySource[node.id]] != null
          ? idMap[defaultTargetBySource[node.id]]
          : undefined,
      tableEntity: stepData.saveVariable || undefined,
      user: stepData.user,
      releaseCommerceAfterResponse:
        stepData.user === "COMMERCE" &&
        stepData.releaseCommerceAfterResponse === true,
      templateName: stepData.templateName || undefined,
      templateLanguage: stepData.templateLanguage,
      transitionMessage: stepData.transitionMessage || undefined,
      templateParameters: regularParams.length > 0 ? regularParams : undefined,
      templateRequiredButtonId: stepData.templateRequiredButtonId || undefined,
      variables: specialParams.length > 0 ? specialParams.join(",") : undefined,
      tableValue: tableValue || undefined,
      waFlowId: stepData.waFlowId || undefined,
      conditionalMessages: serializeConditionalMessagesForPayload(
        conditionalMessages
      ),
      stepsEdition: stepsEdition.length > 0 ? stepsEdition : undefined,
      autoCompleteOnEnter: stepData.autoCompleteOnEnter === true,
      autoCancelOnEnter: stepData.autoCancelOnEnter === true,
      clientCompletionNotificationMessage:
        stepData.clientCompletionNotificationMessage.trim()
          ? stepData.clientCompletionNotificationMessage
          : null,
      commerceCompletionNotificationMessage:
        stepData.commerceCompletionNotificationMessage.trim()
          ? stepData.commerceCompletionNotificationMessage
          : null,
      adminNotificationMessage: stepData.adminNotificationMessage.trim()
        ? stepData.adminNotificationMessage
        : null,
    };
  });

  return {
    step_connection: {
      name,
      description: description ?? "",
      flowRole,
    },
    step_conversations,
    _editorState: { nodes, edges },
  };
};

const REVERSE_TABLE_VALUE_MAP = Object.fromEntries(
  Object.entries(ACTION_TABLE_VALUE_MAP).map(([action, tv]) => [tv, action])
);

const NODES_PER_COLUMN = 4;
const CASCADE_X_OFFSET = 350;
const CASCADE_Y_OFFSET = 150;
const CASCADE_X_START = 80;
const CASCADE_Y_START = 120;

/**
 * Convierte la respuesta del GET /v1/step-connections/{uuid} al estado interno del editor.
 * Es la funcion inversa de `buildWhatsappFlowPayload`.
 *
 * @param {{ step_connection: object, step_conversations: object[] }} apiResponse
 * @returns {{ name: string, description: string, flowRole: string, nodes: object[], edges: object[] }}
 */
export const parseWhatsappFlowPayload = (apiResponse) => {
  const conn = apiResponse?.step_connection || {};
  const conversations = Array.isArray(apiResponse?.step_conversations)
    ? apiResponse.step_conversations
    : [];

  const name = conn.name || "";
  const description = conn.description || "";
  const flowRole = conn.flowRole || "PRIMARY";

  const sorted = [...conversations].sort(
    (a, b) => (a.order ?? Infinity) - (b.order ?? Infinity)
  );

  const uuidToInternal = {};
  sorted.forEach((step, idx) => {
    uuidToInternal[String(step.id)] = `step-${idx + 1}`;
  });

  const nodes = sorted.map((step, idx) => {
    const internalId = `step-${idx + 1}`;
    const col = Math.floor(idx / NODES_PER_COLUMN);
    const row = idx % NODES_PER_COLUMN;

    const buttons =
      step.buttonsLabels && typeof step.buttonsLabels === "string"
        ? step.buttonsLabels.split(",").filter(Boolean)
        : [];

    const incomingTemplateParams = Array.isArray(step.templateParameters)
      ? step.templateParameters
      : [];
    const parameterKeys = incomingTemplateParams.filter(
      (k) => !SPECIAL_VARIABLE_KEYS.includes(stripBraces(k))
    );

    const incomingSpecials =
      step.variables && typeof step.variables === "string"
        ? step.variables.split(",").map((v) => v.trim()).filter(Boolean)
        : [];
    const manualSpecialVariables = Array.from(
      new Set(incomingSpecials.filter((k) => MANUAL_SPECIAL_VARIABLE_KEYS.includes(k)))
    );

    const actions = [];
    if (step.tableValue && REVERSE_TABLE_VALUE_MAP[step.tableValue]) {
      actions.push(REVERSE_TABLE_VALUE_MAP[step.tableValue]);
    }

    const conditionalMessages = parseApiConditionalMessages(step);
    const stepsEdition = parseApiStepsEdition(step, uuidToInternal);

    return {
      id: internalId,
      type: "whatsappStep",
      position: {
        x: CASCADE_X_START + col * CASCADE_X_OFFSET,
        y: CASCADE_Y_START + row * CASCADE_Y_OFFSET,
      },
      data: normalizeWhatsappStepData({
        name: step.name || "",
        message: step.templateText || "",
        messageType: step.template || DEFAULT_MESSAGE_TYPE,
        validations: step.validations || "",
        buttons,
        templateName: step.templateName || "",
        parameterKeys,
        manualSpecialVariables,
        saveVariable: step.tableEntity || "",
        actions,
        description: step.description || "",
        templateTextError: step.templateTextError || "",
        user: step.user || "COMMERCE",
        releaseCommerceAfterResponse:
          step.releaseCommerceAfterResponse === true,
        templateLanguage: step.templateLanguage || "es",
        transitionMessage: step.transitionMessage || "",
        templateRequiredButtonId: step.templateRequiredButtonId || "",
        waFlowId: step.waFlowId || "",
        conditionalMessages,
        stepsEdition,
        autoCompleteOnEnter: step.autoCompleteOnEnter === true,
        autoCancelOnEnter: step.autoCancelOnEnter === true,
        clientCompletionNotificationMessage:
          step.clientCompletionNotificationMessage || "",
        commerceCompletionNotificationMessage:
          step.commerceCompletionNotificationMessage || "",
        adminNotificationMessage: step.adminNotificationMessage || "",
      }),
    };
  });

  const edges = [];
  const stepsWithOptions = new Set();

  sorted.forEach((step, idx) => {
    const sourceId = uuidToInternal[String(step.id)];
    if (!step.options || typeof step.options !== "object") return;

    const optionEntries = Object.entries(step.options);
    if (optionEntries.length === 0) return;

    stepsWithOptions.add(idx);
    optionEntries.forEach(([, targetUuid], optIdx) => {
      const targetId = uuidToInternal[String(targetUuid)];
      if (!targetId) return;
      edges.push({
        id: `e-${sourceId}-option-${optIdx}-${targetId}`,
        type: "smoothstep",
        source: sourceId,
        target: targetId,
        sourceHandle: `${OPTION_HANDLE_PREFIX}${optIdx}`,
      });
    });
  });

  // Transiciones lineales: si el backend trae `nextStep` explícito (modo nuevo) se
  // reconstruyen desde ahí; si ningún paso lo trae (flujos viejos) se cae al modo legacy
  // por orden consecutivo para no romper su visualización.
  const hasExplicitNext = sorted.some((step) => step.nextStep != null);

  if (hasExplicitNext) {
    sorted.forEach((step) => {
      if (step.nextStep == null) return;
      const sourceId = uuidToInternal[String(step.id)];
      const targetId = uuidToInternal[String(step.nextStep)];
      if (!sourceId || !targetId) return;
      edges.push({
        id: `e-${sourceId}-${targetId}`,
        type: "smoothstep",
        source: sourceId,
        target: targetId,
      });
    });
  } else {
    sorted.forEach((step, idx) => {
      if (stepsWithOptions.has(idx)) return;
      if (idx >= sorted.length - 1) return;
      const sourceId = uuidToInternal[String(step.id)];
      const targetId = uuidToInternal[String(sorted[idx + 1].id)];
      edges.push({
        id: `e-${sourceId}-${targetId}`,
        type: "smoothstep",
        source: sourceId,
        target: targetId,
      });
    });
  }

  return { name, description, flowRole, nodes, edges };
};

/**
 * Prepara el payload para enviar al backend. Elimina `_editorState` que solo es para el editor.
 */
export const prepareStepConnectionPayload = (payload) => {
  if (!payload || typeof payload !== "object") return payload;
  const { _editorState: _omit, ...rest } = payload;
  return rest;
};

export const saveWhatsappFlow = (payload) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
};

export const loadWhatsappFlow = () => {
  const storedFlow = localStorage.getItem(STORAGE_KEY);
  if (!storedFlow) return null;

  try {
    const parsedFlow = JSON.parse(storedFlow);
    if (!Array.isArray(parsedFlow.nodes) || !Array.isArray(parsedFlow.edges)) {
      return null;
    }
    if (parsedFlow.description == null) parsedFlow.description = "";
    return parsedFlow;
  } catch (error) {
    return null;
  }
};
